import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { ProductKey, Url } from '@atlassiansox/bxpkit-core';
import { merge } from 'lodash';
import { WorkspacesType } from '../../../model/workspaces';
import {
  FormDataN2E,
  LocaleType,
  ProductPayloadActivateType,
  SitesType
} from '../types';
import { buildSignupUrlAction, getSignupContext } from './';
import { getConsents } from './legal-consents';

type BuildN2EPayloadType = {
  readonly locale: LocaleType;
  readonly formData: FormDataN2E;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly sites?: SitesType;
  readonly bbworkspaces?: WorkspacesType[];
  readonly createAnalyticsEvent: CreateUIAnalyticsEvent;
};
type ProductsArrayType = {
  readonly product?: string;
  readonly productKey?: string;
  readonly accountName: string;
  readonly edition?: string;
};

type BuildN2EPayloadReturnType = {
  readonly consent: {
    readonly site: string;
    readonly locale: string;
    readonly formUrl: string;
    readonly consents: {
      readonly key: string;
      readonly displayedText: string | undefined;
      readonly granted: boolean;
    }[];
  };
  readonly timezone: string;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly cloudName: string;
  readonly cloudId?: string;
  readonly products: ProductsArrayType[];
  readonly signupContext?: string;
  readonly activateProducts: boolean;
  readonly adminUsername: string;
  readonly advancedSettings: {
    readonly additionalProvisioningData: {
      readonly workspaceUuid?: string;
    };
  };
};

export async function buildN2EPayload({
  formData,
  locale,
  firstName,
  lastName,
  email,
  sites,
  bbworkspaces,
  createAnalyticsEvent
}: BuildN2EPayloadType): Promise<BuildN2EPayloadReturnType> {
  const { productsExisting, bbWorkspaceName } = formData;
  const cloudId =
    sites?.find((site) => site.displayName === formData.cloudName)?.cloudId ||
    undefined;
  const productsAndEditions =
    // @ts-ignore
    sites?.find((site) => site.displayName === formData.cloudName)
      ?.productsAndEditions || undefined;
  const cloudName = formData?.cloudName || '';
  const products = formData?.productsNew || [];
  const signupContext = getSignupContext({ cloudName, createAnalyticsEvent });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const queryParameters = Url.getUrlParams() || {};
  const signupDevopsSource = queryParameters.hasOwnProperty('signupSource')
    ? queryParameters.signupSource
    : undefined;
  const consentChecked = formData?.marketingConsent ?? true;
  const consent = getConsents(locale, consentChecked);
  // will need to change with new endpoint
  const existingWorkspace = bbworkspaces?.find(
    (workspace) => workspace.workspace.name === bbWorkspaceName
  );
  // activations require a 'productKey' instead of 'product' keys
  const activateProducts: ProductPayloadActivateType[] = products.map(
    (product) => {
      const retObj = {
        productKey: product.product,
        accountName: product.accountName
      };
      if (product.product !== 'opsgenie') {
        Object.assign(retObj, { edition: product.edition });
      }
      return retObj;
    }
  );

  // Always include JSW in N2E existing site cases
  // so that Cloud Provisioner can link BB+JSW
  const jswInActivatePayload = !!activateProducts.find(
    (product) => product.productKey === ProductKey.JIRA_SOFTWARE
  );

  if (!jswInActivatePayload && cloudId && productsAndEditions) {
    // @ts-ignore
    const { productEdition } = productsAndEditions.find(
      (obj) => obj.productKey === ProductKey.JIRA_SOFTWARE
    );

    activateProducts.push({
      productKey: ProductKey.JIRA_SOFTWARE,
      accountName: cloudName,
      edition: productEdition
    });
  }
  const workspaceUuid = undefined;
  const payload = {
    consent,
    timezone,
    email,
    firstName,
    lastName,
    cloudName,
    products,
    signupContext,
    activateProducts: false,
    adminUsername: 'admin',
    advancedSettings: {
      suppressEmail: true,
      additionalProvisioningData: {
        dataRegion: 'main',
        signupDevops: true,
        workspaceUuid
      }
    }
  };

  if (signupDevopsSource) {
    Object.assign(payload.advancedSettings.additionalProvisioningData, {
      signupDevopsSource
    });
  }

  if (
    productsExisting &&
    productsExisting.length > 0 &&
    !productsExisting.includes(ProductKey.BITBUCKET)
  ) {
    Object.assign(payload, {
      activateProducts: true,
      cloudId,
      products: activateProducts
    });
  }
  // existing workspaces should never be activate products flow, the other 3 should be new products
  if (existingWorkspace) {
    Object.assign(payload.advancedSettings.additionalProvisioningData, {
      workspaceUuid: existingWorkspace.workspace.uuid
    });
    const bbProduct = {
      product: ProductKey.BITBUCKET,
      accountName: cloudName
    };
    Object.assign(payload, { products: [...payload.products, bbProduct] });
  }

  merge(
    payload,
    buildSignupUrlAction(activateProducts.map((p) => p.productKey))
  );
  return Promise.resolve(payload);
}
